/**
 * MtgCraft backup parser
 *
 * Parses MtgCraft JSON backup format for:
 * - Single deck (mtgcraft_deck)
 * - Multiple decks (mtgcraft_decks)
 * - Single list (mtgcraft_list)
 * - Multiple lists (mtgcraft_lists)
 * - Collection (legacy mtgcraft format)
 */
import type { CollectionCardBackup, CollectionFolderBackup, DeckBackupData, ListBackupData } from '../types';
import { isCollectionBackup, isMultiDeckBackup, isMultiListBackup, isSingleDeckBackup, isSingleListBackup } from '../types';

// ============================================================================
// Types
// ============================================================================

export type MtgCraftBackupType = 'single_deck' | 'multi_deck' | 'single_list' | 'multi_list' | 'collection' | 'unknown';

export interface MtgCraftBackupParseResult {
  type: MtgCraftBackupType;
  data: unknown;
  errors: string[];
}

export interface DeckBackupParseResult {
  type: 'single_deck' | 'multi_deck';
  decks: DeckBackupData[];
  errors: string[];
}

export interface ListBackupParseResult {
  type: 'single_list' | 'multi_list';
  lists: ListBackupData[];
  errors: string[];
}

export interface CollectionBackupParseResult {
  cards: CollectionCardBackup[];
  folders: CollectionFolderBackup[];
  errors: string[];
}

// ============================================================================
// Detection
// ============================================================================

/**
 * Detect the type of MtgCraft backup from JSON text
 */
export function detectMtgCraftBackupType(text: string): MtgCraftBackupType {
  const trimmed = text.trim();

  // Must be JSON
  if (!trimmed.startsWith('{')) {
    return 'unknown';
  }

  try {
    const parsed = JSON.parse(trimmed);

    // Check for typed backups (BackupBase discriminator)
    if (isSingleDeckBackup(parsed)) return 'single_deck';
    if (isMultiDeckBackup(parsed)) return 'multi_deck';
    if (isSingleListBackup(parsed)) return 'single_list';
    if (isMultiListBackup(parsed)) return 'multi_list';
    if (isCollectionBackup(parsed)) return 'collection';

    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Check if text looks like a MtgCraft backup
 */
export function isMtgCraftBackup(text: string): boolean {
  return detectMtgCraftBackupType(text) !== 'unknown';
}

// ============================================================================
// Parsers
// ============================================================================

/**
 * Parse a MtgCraft backup of any type
 */
export function parseMtgCraftBackup(text: string): MtgCraftBackupParseResult {
  const type = detectMtgCraftBackupType(text);

  if (type === 'unknown') {
    return {
      type: 'unknown',
      data: null,
      errors: ['Unable to detect MtgCraft backup format'],
    };
  }

  try {
    const parsed = JSON.parse(text);
    return {
      type,
      data: parsed,
      errors: [],
    };
  } catch (e) {
    return {
      type: 'unknown',
      data: null,
      errors: [`Invalid JSON: ${e instanceof Error ? e.message : 'Unknown error'}`],
    };
  }
}

/**
 * Parse a deck backup (single or multi)
 */
export function parseDeckBackup(text: string): DeckBackupParseResult {
  const errors: string[] = [];
  const trimmed = text.trim();

  try {
    const parsed = JSON.parse(trimmed);

    if (isSingleDeckBackup(parsed)) {
      const validation = validateDeckBackupData(parsed.deck);
      if (validation.errors.length > 0) {
        errors.push(...validation.errors);
      }
      return {
        type: 'single_deck',
        decks: [parsed.deck],
        errors,
      };
    }

    if (isMultiDeckBackup(parsed)) {
      const decks: DeckBackupData[] = [];
      for (let i = 0; i < parsed.decks.length; i++) {
        const deck = parsed.decks[i];
        const validation = validateDeckBackupData(deck);
        if (validation.errors.length > 0) {
          errors.push(...validation.errors.map((e) => `Deck ${i + 1}: ${e}`));
        }
        decks.push(deck);
      }
      return {
        type: 'multi_deck',
        decks,
        errors,
      };
    }

    return {
      type: 'single_deck',
      decks: [],
      errors: ['Not a valid deck backup format'],
    };
  } catch (e) {
    return {
      type: 'single_deck',
      decks: [],
      errors: [`Invalid JSON: ${e instanceof Error ? e.message : 'Unknown error'}`],
    };
  }
}

/**
 * Parse a list backup (single or multi)
 */
export function parseListBackup(text: string): ListBackupParseResult {
  const errors: string[] = [];
  const trimmed = text.trim();

  try {
    const parsed = JSON.parse(trimmed);

    if (isSingleListBackup(parsed)) {
      const validation = validateListBackupData(parsed.list);
      if (validation.errors.length > 0) {
        errors.push(...validation.errors);
      }
      return {
        type: 'single_list',
        lists: [parsed.list],
        errors,
      };
    }

    if (isMultiListBackup(parsed)) {
      const lists: ListBackupData[] = [];
      for (let i = 0; i < parsed.lists.length; i++) {
        const list = parsed.lists[i];
        const validation = validateListBackupData(list);
        if (validation.errors.length > 0) {
          errors.push(...validation.errors.map((e) => `List ${i + 1}: ${e}`));
        }
        lists.push(list);
      }
      return {
        type: 'multi_list',
        lists,
        errors,
      };
    }

    return {
      type: 'single_list',
      lists: [],
      errors: ['Not a valid list backup format'],
    };
  } catch (e) {
    return {
      type: 'single_list',
      lists: [],
      errors: [`Invalid JSON: ${e instanceof Error ? e.message : 'Unknown error'}`],
    };
  }
}

/**
 * Parse a collection backup
 */
export function parseCollectionBackup(text: string): CollectionBackupParseResult {
  const errors: string[] = [];
  const trimmed = text.trim();

  try {
    const parsed = JSON.parse(trimmed);

    if (!isCollectionBackup(parsed)) {
      return { cards: [], folders: [], errors: ['Not a valid collection backup format'] };
    }

    if (!Array.isArray(parsed.cards)) {
      return { cards: [], folders: [], errors: ['Missing or invalid cards array'] };
    }

    // Validate cards
    for (let i = 0; i < parsed.cards.length; i++) {
      const card = parsed.cards[i];
      if (!card.scryfall_id || typeof card.scryfall_id !== 'string') {
        errors.push(`Card at index ${i}: missing scryfall_id`);
      }
      if (typeof card.quantity !== 'number' || card.quantity < 1) {
        errors.push(`Card at index ${i}: invalid quantity`);
      }
    }

    return {
      cards: parsed.cards,
      folders: Array.isArray(parsed.folders) ? parsed.folders : [],
      errors,
    };
  } catch (e) {
    return {
      cards: [],
      folders: [],
      errors: [`Invalid JSON: ${e instanceof Error ? e.message : 'Unknown error'}`],
    };
  }
}

// ============================================================================
// Validation
// ============================================================================

interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Validate deck backup data structure
 */
function validateDeckBackupData(deck: DeckBackupData): ValidationResult {
  const errors: string[] = [];

  if (!deck.name || typeof deck.name !== 'string') {
    errors.push('Missing or invalid deck name');
  }

  if (!deck.cards || !Array.isArray(deck.cards)) {
    errors.push('Missing or invalid cards array');
  } else {
    for (let i = 0; i < deck.cards.length; i++) {
      const card = deck.cards[i];
      if (!card.scryfall_id || typeof card.scryfall_id !== 'string') {
        errors.push(`Card at index ${i}: missing scryfall_id`);
      }
      if (typeof card.quantity !== 'number' || card.quantity < 1) {
        errors.push(`Card at index ${i}: invalid quantity`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate list backup data structure
 */
function validateListBackupData(list: ListBackupData): ValidationResult {
  const errors: string[] = [];

  if (!list.name || typeof list.name !== 'string') {
    errors.push('Missing or invalid list name');
  }

  if (!list.cards || !Array.isArray(list.cards)) {
    errors.push('Missing or invalid cards array');
  } else {
    for (let i = 0; i < list.cards.length; i++) {
      const card = list.cards[i];
      if (!card.name || typeof card.name !== 'string') {
        errors.push(`Card at index ${i}: missing name`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
